import { Button, TextInput, Spacing, FormField, Checkbox } from 'zens-storybook'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'
import { object, string } from 'yup'
import { ApiError, useProfile, useStore } from 'zens-store'
import { useRouter, useNumberParams, useParams, Redirect } from 'zens-router'
import { AuthPanel, AuthPasswordHelp } from '@dms/component'
import { ValidationError } from '@dms/helper'

const styles = StyleSheet.create({
  input: {
    marginBottom: Spacing.XS,
    minHeight: 92,
  },
  email: {
    marginHorizontal: Spacing.XL,
    marginBottom: Spacing.L,
  },
  button: {
    minWidth: 100,
  },
})

const AuthPassword = () => {
  const { t } = useTranslation()
  const [{ companyId }] = useNumberParams()
  const [{ token }] = useParams()
  const [password, setPassword] = useState('')
  const [remember, setRemember] = useState(true)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const router = useRouter()
  const { auth, message } = useStore()
  const profile = useProfile({ token })

  const schema = object({
    email: string().required(t('Email address is required')),
    password: string().required(t('A password is required')),
  })

  useEffect(() => {
    if (profile.data?.id && auth.profile?.id === profile.data?.id) {
      console.debug('Already logged in correctly')
      if (companyId) router.push(`/companies/${companyId}`)
      else router.push('/')
    }
  }, [profile.data])

  useEffect(() => {
    if (profile.isError) {
      const { message: errorMessage } = profile.error as Error
      console.debug(`AUTHPASSWORD: Profile load failed: ${errorMessage}`)
      message.add({
        message: `${t('Profile data could not be loaded')}. ${errorMessage}`,
        status: 'error',
      })
    }
  }, [profile.isError])

  useEffect(() => {
    setErrors({})
  }, [password])

  const reset = async () => {
    try {
      schema.validateSync({ email: profile.data?.email, password })
    } catch (error) {
      const { message, path } = error as ValidationError
      setErrors({ [path]: message })
      return
    }
    setLoading(true)
    try {
      if (!token || !profile.data?.data.name || !profile.data?.email) throw new Error('Invalid data')
      setErrors({})
      await auth.updatePassword({
        token,
        email: profile.data?.email,
        password,
      })
      if (remember) await auth.rememberAuth()
      router.push('/')
      setLoading(false)
    } catch (error) {
      const { message: errorMessage, fields } = error as ApiError
      console.debug(`AUTH: Password reset failed: ${errorMessage}`)
      if (fields) {
        setErrors(fields)
      } else {
        message.add({
          message: `${t('Password reset failed')}. ${errorMessage}`,
          status: 'error',
        })
      }
      setLoading(false)
    }
  }

  if (!profile.isFetched) return <View />
  if (profile.data?.email === undefined) {
    return <Redirect href={{ pathname: '/forbidden' }} />
  }

  return (
    <AuthPanel
      title={t('Reset password')}
      bottom={
        <Button
          title={t('Reset password')}
          style={styles.button}
          onPress={reset}
          testID="AuthPassword.Submit"
          disabled={loading}
        />
      }
    >
      <FormField disabled error={errors.email} style={styles.email}>
        <TextInput value={profile.data?.email} disabled error={!!errors.email} round />
      </FormField>
      <FormField
        label={t('Choose new password')}
        error={errors.password}
        style={styles.input}
        help={<AuthPasswordHelp value={password} />}
      >
        <TextInput
          placeholder={t('Enter your password here')}
          value={password}
          onChangeText={setPassword}
          onSubmitEditing={reset}
          autoCapitalize="none"
          autoComplete="password"
          error={!!errors.password}
          secureTextEntry
          autoFocus
          testID="AuthPassword.Password"
        />
      </FormField>
      <Checkbox
        label={t('Keep me signed in')}
        value={remember}
        onValueChange={setRemember}
        testID="AuthPassword.Remember"
      />
    </AuthPanel>
  )
}

export default AuthPassword
